/* eslint-disable no-top/no-top-level-side-effects -- expo entrypoint */
import * as Sentry from "@sentry/react";
import { makeFetchTransport } from "@sentry/browser";
import { captureConsoleIntegration, dedupeIntegration } from "@sentry/core";
import { mirrorWatchdogReportToDevConsole } from "#dev-watchdog-sink";
import { createSentryEventMirror, subscribeToSentryEvents } from "#sentry-event-listeners";
import {
  createSentryAnalyticsLinkProcessor,
  type SentryAnalyticsIdentity,
  setSentryAnalyticsLink,
} from "#sentry-analytics-link";
import type { SentryDiagnosticReportInput } from "#sentry-diagnostics-types";
import { ENV } from "@-/com.npmjs.varlock/env";
import type { ComponentType } from "react";
import { trimSentryTransaction, withSentryEnvelopeSizeLimit } from "#sentry-payload-limits";

// `EXPO_PUBLIC_HANIVERSE_WORKER_URL` is optional (no dev Infisical value; supplied by the dev orchestrator
// / EAS at build time). The Sentry tunnel routes web envelopes through the haniverse-worker to dodge ad
// blockers — when the URL is absent (e.g. a plain type-check build), skip the tunnel; Sentry still
// posts directly to its ingest.
const sentryWorkerBaseUrl = ENV.EXPO_PUBLIC_HANIVERSE_WORKER_URL;
const sentryTunnelUrl =
  sentryWorkerBaseUrl !== undefined && sentryWorkerBaseUrl.length > 0
    ? `${sentryWorkerBaseUrl.replace(/\/+$/, "")}/sentry`
    : undefined;

// Trace sampling by build variant, mirroring the native setup: staging (our
// low-volume instrumentation build) traces everything, production a small
// sample, dev/other off. `EXPO_PUBLIC_APP_VARIANT` is babel-inlined to a literal.
const PERF_TRACES_SAMPLE_RATE =
  ENV.EXPO_PUBLIC_APP_VARIANT === "staging"
    ? 1
    : ENV.EXPO_PUBLIC_APP_VARIANT === "production"
      ? 0.1
      : 0;

// Only the two DEPLOYED web variants report to Sentry.
//
// `__DEV__` alone is NOT a sufficient gate. It is false in every `NODE_ENV=production`
// build, which includes the Maestro-web CI stacks — those serve the app from a LOCAL
// Metro dev server (`http://127.0.0.1:<port>`), so `__DEV__` was false, Sentry was live,
// and with `EXPO_PUBLIC_APP_VARIANT` unset the events tagged themselves
// `environment: production` and landed in the production project indistinguishably from
// real user traffic (HAN-16: 27 events, every one from `127.0.0.1:5811` under
// HeadlessChrome).
//
// Those events are also permanently UNSYMBOLICATED, and unfixably so. The Sentry Metro
// serializer (`getSentryExpoConfig`) stamps a debug ID into every client bundle it builds
// INCLUDING dev-server ones, so the frames carry a debug ID that no upload could ever
// match: a Metro-served bundle is built per-request and never passes through
// `expo export` + `runSentrySourcemapsUpload` (see configs/expo/web/-export-web-shared.ts),
// which is the only thing that uploads maps. Sentry's "no source file with a matching
// Debug ID" hint on such an event describes an unuploadable bundle, not a broken upload —
// deployed `haniverse.app` events symbolicate correctly with full source context.
//
// So gate on the variant instead: a build that is not staging/production has no uploaded
// maps by construction and must not report.
const IS_REPORTING_VARIANT =
  ENV.EXPO_PUBLIC_APP_VARIANT === "staging" || ENV.EXPO_PUBLIC_APP_VARIANT === "production";

Sentry.init({
  dsn: "https://7a6c44951bb00edc9b16d769c7ddf58b@o4508954014908416.ingest.us.sentry.io/4512164631412736",
  enabled: !__DEV__ && IS_REPORTING_VARIANT,

  enableLogs: true,

  // Set explicitly rather than left to the SDK default. Without this the environment is
  // inferred as "production" for ANY non-dev build, which is exactly how the local
  // Maestro-web stacks filed themselves as production traffic.
  environment: ENV.EXPO_PUBLIC_APP_VARIANT ?? "development",

  // `autoInject: false` keeps the feedback widget available (trigger it manually
  // via `Sentry.getFeedback()`) but stops the SDK from injecting the floating
  // "Report a Bug" button into every page.
  //
  // `captureConsoleIntegration` patches the global `console` so every
  // raw `console.error(...)` becomes a Sentry event. The app's
  // `logger(import.meta.location)` routes through LogLayer in production and
  // never touches the console there. Scoped to `error` only.
  // `dedupeIntegration` collapses an event identical to the immediately-preceding
  // one if a third-party integration reports the same failure through both paths.
  // On web it's already a default integration; adding it explicitly is a no-op
  // replacement (Sentry keys integrations by name) and keeps both setups symmetrical.
  beforeSendTransaction: trimSentryTransaction,

  integrations: [
    // `autoInject: false` keeps the floating "Report a Bug" button off every page;
    // the report-bug header control opens the form on demand via `showSentryFeedback()`.
    // Trim it to a single description field + Send button — no name/email prompts.
    Sentry.feedbackIntegration({
      autoInject: false,
      formTitle: "Report a Bug",
      messageLabel: "Description",
      messagePlaceholder: "What went wrong?",
      showEmail: false,
      showName: false,
      submitButtonLabel: "Send",
    }),
    captureConsoleIntegration({ levels: ["error"] }),
    dedupeIntegration(),
    // Performance tracing (web). Only install when we sample; `browserTracingIntegration`
    // opens a pageload/navigation transaction that our query/suspense spans nest under.
    ...(PERF_TRACES_SAMPLE_RATE > 0 ? [Sentry.browserTracingIntegration()] : []),
  ],
  sendDefaultPii: true,
  tracesSampleRate: PERF_TRACES_SAMPLE_RATE,
  transport: (options) =>
    withSentryEnvelopeSizeLimit(makeFetchTransport(options), options.recordDroppedEvent),
  tunnel: sentryTunnelUrl,
});

if (PERF_TRACES_SAMPLE_RATE > 0) {
  // Falls back to "development", NOT "production": an unset variant means a local /
  // CI-harness build, and defaulting it to "production" is what made the Maestro-web
  // events indistinguishable from real traffic in the first place.
  Sentry.setTag("app.variant", ENV.EXPO_PUBLIC_APP_VARIANT ?? "development");
}

// Mirror captured events to the diagnostics recorder (see `subscribeToSentryEvents`).
Sentry.addEventProcessor(createSentryEventMirror());
Sentry.addEventProcessor(createSentryAnalyticsLinkProcessor());

// Structured logs reach Sentry via LogLayer's `SentryTransport` (wired in the
// app LogLayer instance, see `lib.logger`), replacing the former
// `consola-sentry-reporter` bridge.

// `@sentry/react` has no `Sentry.wrap`; the root error boundary / profiling that
// the native `wrap` provides is not wired on web. Pass the component through.
export function sentrySetup<P extends Record<string, unknown>>(
  Component: ComponentType<P>,
): ComponentType<P> {
  return Component;
}

export function captureSentryException(
  exception: unknown,
  captureContext?: Parameters<typeof Sentry.captureException>[1],
): string {
  // Sentry is disabled in dev (see `Sentry.init` above), which would otherwise make every
  // watchdog report silent exactly where a developer needs to see it. Mirror those to the
  // console first; a no-op in production and for non-watchdog exceptions.
  mirrorWatchdogReportToDevConsole(exception);
  return Sentry.captureException(exception, captureContext);
}

// Opens Sentry's built-in user-feedback form (configured above to a single
// description field + Send button). The feedback integration builds the dialog
// lazily, so grab it via `getFeedback()`, mount the form, and open it.
export function showSentryFeedback(): void {
  const feedback = Sentry.getFeedback();
  if (feedback === undefined) {
    return;
  }

  feedback
    .createForm()
    .then((form) => {
      form.appendToDom();
      form.open();
    })
    .catch(() => {
      // Best-effort: failing to build the feedback form must never crash the app.
    });
}
/* eslint-enable no-top/no-top-level-side-effects -- expo entrypoint */

export { subscribeToSentryEvents };
export type {
  SentryDiagnosticReportInput,
  SentryEventListener,
  SentryEventSummary,
} from "#sentry-diagnostics-types";

/** The id of the most recently captured event, for correlating a diagnostic report with an issue. */
export function getSentryLastEventId(): string | undefined {
  return Sentry.lastEventId();
}

// The web transport enforces `SENTRY_ENVELOPE_BYTE_BUDGET` on the WHOLE envelope, attachments
// included, and drops anything over it. Keep the attachment well under that so the message event
// itself (with the report URL in `extra`) always gets through; the worker upload carries the
// full report regardless.
const WEB_ATTACHMENT_BYTE_LIMIT = 500_000;

function attachmentByteLength(data: string | Uint8Array): number {
  return typeof data === "string" ? new TextEncoder().encode(data).byteLength : data.byteLength;
}

/**
 * Sends a diagnostic report as an `info` message event with the report attached (when it fits
 * the web envelope budget). Resolves the event id, or `null` when the SDK is disabled (dev / CI
 * builds) so callers can fall back to downloading the file.
 */
export async function captureSentryDiagnosticReport(
  input: SentryDiagnosticReportInput,
): Promise<string | null> {
  if (!Sentry.isEnabled()) {
    return null;
  }
  const eventId = Sentry.withScope((scope) => {
    if (
      input.attachment !== undefined &&
      attachmentByteLength(input.attachment.data) <= WEB_ATTACHMENT_BYTE_LIMIT
    ) {
      scope.addAttachment(input.attachment);
    }
    return Sentry.captureMessage(input.message, {
      extra: input.extra,
      level: "info",
      tags: input.tags,
    });
  });
  await Sentry.flush(10_000);
  return eventId;
}

/**
 * Identifies the Sentry user by the analytics distinct id and tags each later event with the
 * current PostHog session, so an issue links to its person and session replay. `null` clears both
 * (sign-out).
 */
export function setSentryAnalyticsIdentity(identity: SentryAnalyticsIdentity | null): void {
  setSentryAnalyticsLink(identity);
  Sentry.setUser(identity === null ? null : { id: identity.distinctId });
}
